import { useCallback } from 'react';
import { nanoid } from 'nanoid';

import { useSearchspring } from '@hooks';

// Beacon 1.0: https://searchspring.zendesk.com/hc/en-us/articles/360052114591-Beacon-API-Recommendations
// Beacon 2.0: https://docs.searchspring.com/reference/beacon-tracking
const BEACON_V2_BASE = 'https://analytics.searchspring.net/beacon/v2';
const BEACON_V2_INITIATOR = 'Brumate/Searchspring Beacon/2.0';

export const useSearchspringBeacon = () => {
  const { requestSearchspring } = useSearchspring();
  // const customer = useCustomer();
  // const customerId = customer?.id || '';

  const getIdOrGenerateId = useCallback(async (name) => {
    try {
      if (!name) {
        return undefined;
      }

      let id = null;
      document.cookie?.split(/\s*;\s*/).find((item) => {
        const [key, value] = item.split(/\s*=\s*/);
        if (key !== name) return false;
        id = value;
        return true;
      });

      if (id) {
        return id;
      }
      const newId = nanoid();
      document.cookie = `${name}=${newId}`;
      return newId;
    } catch (error) {
      console.error('Error with getIdOrGenerateId', error.message);
      throw new Error(error.message);
    }
  }, []);

  const sendEventsToBeacon = useCallback(
    async ({
      profileTypes = [], // 'profile.render', 'profile.impression', 'profile.click'
      productTypes = [], // 'profile.product.render', 'profile.product.impression', 'profile.product.click'
      tag, // e.g. 'view-cart', 'product-page'
      placement, // e.g. 'basket-page', 'product-page'
      products = [], // loaded products on mount; required w/ 'profile.product.render'
      visibleProducts = [], // products visible in the viewport; required w/ 'profile.product.impression'
      clickedProduct, // product clicked; required w/ 'profile.product.click'
      seed, // e.g. [{ sku: '123456' }]
    }) => {
      try {
        if (!tag) {
          throw new Error('No tag provided to sendEventsToBeacon');
        }
        if (!placement) {
          throw new Error('No placement provided to sendEventsToBeacon');
        }
        if (!profileTypes.length && !productTypes.length) {
          throw new Error(
            'No profileTypes or productTypes provided to sendEventsToBeacon'
          );
        }
        if (
          productTypes.includes('profile.product.render') &&
          !products.length
        ) {
          throw new Error('products are required for profile.product.render');
        }
        if (
          productTypes.includes('profile.product.impression') &&
          !visibleProducts.length
        ) {
          throw new Error(
            'visibleProducts are required for profile.product.impression'
          );
        }
        if (productTypes.includes('profile.product.click') && !clickedProduct) {
          throw new Error(
            'clickedProduct is required for profile.product.click'
          );
        }

        // const shopper = atob(customerId)?.split('/').pop() || null;
        const userId = await getIdOrGenerateId('ssUserId');
        const sessionId = await getIdOrGenerateId('ssSessionIdNamespace');
        const pageLoadId = nanoid();

        const pids = {};

        const context = {
          website: {
            trackingCode: process.env.NEXT_PUBLIC_SEARCHSPRING_SITE_ID,
          },
          userId,
          sessionId,
          pageLoadId,
        };

        const profileEvents = profileTypes.map((type) => {
          const id = nanoid();
          pids[type] = id;

          return {
            type,
            category: 'searchspring.recommendations.user-interactions',
            id,
            pid: null,
            event: {
              profile: {
                tag,
                placement,
                seed,
              },
              context: {
                type: 'product-recommendation',
                tag,
                placement,
              },
            },
            context,
          };
        });

        const productEvents = productTypes.reduce((acc, type) => {
          let _products;
          if (type === 'profile.product.render') _products = products;
          else if (type === 'profile.product.impression')
            _products = visibleProducts;
          else if (type === 'profile.product.click')
            _products = [clickedProduct];
          else _products = [];

          const events = _products.map((product) => {
            return {
              type,
              category: 'searchspring.recommendations.user-interactions',
              id: nanoid(),
              pid: pids[type.replace('.product.', '.')] || null,
              event: {
                product: {
                  id: product.id,
                  seed,
                  mappings: product.mappings,
                },
                context: {
                  type: 'product-recommendation',
                  tag,
                  placement,
                },
              },
              context,
            };
          });
          return [...acc, ...events];
        }, []);

        const events = [...profileEvents, ...productEvents];

        const data = await requestSearchspring({
          action: 'sendToBeacon',
          params: { events },
        });

        return data;
      } catch (error) {
        console.error(`Error with useSearchspringBeacon: ${error.message}`);
        throw error;
      }
    },
    []
  );

  /**
   * Beacon 2.0: sends a single event to the resource-specific endpoint.
   * Uses reference-based payloads (responseId) instead of batched product metadata.
   * @see https://docs.searchspring.com/reference/beacon-migration-guide
   *
   * @param {Object} params
   * @param {'render'|'impression'|'clickthrough'} params.eventType - Beacon 2.0 endpoint
   * @param {string} params.responseId - From the Recommendations API response (required for all)
   * @param {string} params.tag - Recommendation profile tag (e.g. 'home-recs', 'similar')
   * @param {Array<{type: string, uid: string, parentId: string}>} [params.results] - For impression/clickthrough: product refs. Omit for render.
   * @param {Array} [params.banners] - For impression only; use [] if none
   * @param {boolean} [params.dev] - If true, events are validated but not reported (sandbox)
   */
  const sendEventsToBeaconV2 = useCallback(
    async ({
      eventType,
      responseId,
      tag,
      results = null,
      banners = [],
      dev = false,
    }) => {
      const siteId = process.env.NEXT_PUBLIC_SEARCHSPRING_SITE_ID;
      if (!siteId) {
        throw new Error('NEXT_PUBLIC_SEARCHSPRING_SITE_ID is not defined');
      }
      if (!responseId) {
        throw new Error('responseId is required for Beacon 2.0');
      }
      if (!tag) {
        throw new Error('tag is required for Beacon 2.0');
      }
      if (
        (eventType === 'impression' || eventType === 'clickthrough') &&
        (!results || !Array.isArray(results))
      ) {
        throw new Error(
          'results array is required for Beacon 2.0 impression and clickthrough'
        );
      }

      const userId = await getIdOrGenerateId('ssUserId');
      const sessionId = await getIdOrGenerateId('ssSessionIdNamespace');
      const pageLoadId = nanoid();

      const context = {
        userId,
        sessionId,
        pageLoadId,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        initiator: BEACON_V2_INITIATOR,
      };
      if (dev) {
        context.dev = 'true';
      }

      const endpoint = `${BEACON_V2_BASE}/${siteId}/recommendations/${eventType}`;
      const data =
        eventType === 'render'
          ? { tag, responseId }
          : {
              responseId,
              tag,
              results,
              ...(eventType === 'impression' && { banners }),
            };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ context, data }),
        keepalive: true,
      });

      if (!res.ok) {
        const errBody = await res.text();
        const msg = `Searchspring Beacon 2.0 ${eventType} failed: ${res.status} ${res.statusText}`;
        throw new Error(errBody ? `${msg} - ${errBody}` : msg);
      }

      return res.json().catch(() => ({}));
    },
    [getIdOrGenerateId]
  );

  /**
   * Beacon 2.0 category / search listing endpoints (no `tag`; uses responseId from Search or Category API).
   * @see https://docs.searchspring.com/reference/category-tracking-guide
   * @see https://docs.searchspring.com/reference/search-tracking-guide
   *
   * @param {Object} params
   * @param {'category'|'search'} params.scope
   * @param {'render'|'impression'|'clickthrough'|'redirect'|'addtocart'} params.eventType
   * @param {string} params.responseId
   * @param {Array} [params.results]
   * @param {Array} [params.banners]
   * @param {string} [params.redirect] - search/redirect only
   * @param {boolean} [params.dev]
   */
  const sendListingBeaconV2 = useCallback(
    async ({
      scope,
      eventType,
      responseId,
      results,
      banners,
      redirect,
      dev,
    }) => {
      const siteId = process.env.NEXT_PUBLIC_SEARCHSPRING_SITE_ID;
      if (!siteId) {
        throw new Error('NEXT_PUBLIC_SEARCHSPRING_SITE_ID is not defined');
      }
      if (!responseId) {
        throw new Error('responseId is required for listing Beacon 2.0');
      }
      if (scope !== 'category' && scope !== 'search') {
        throw new Error('scope must be "category" or "search"');
      }

      const resource = scope === 'category' ? 'category' : 'search';

      if (eventType === 'redirect' && scope !== 'search') {
        throw new Error('redirect event is only valid for search scope');
      }

      if (eventType === 'render') {
        // data is only responseId
      } else if (eventType === 'redirect') {
        if (!redirect) {
          throw new Error('redirect URL is required for search/redirect');
        }
      } else if (eventType === 'impression') {
        if (!Array.isArray(results)) {
          throw new Error(
            'results array is required for listing impression (may be empty)'
          );
        }
        if (!Array.isArray(banners)) {
          throw new Error(
            'banners array is required for listing impression (may be empty)'
          );
        }
      } else if (eventType === 'clickthrough') {
        const hasResults = Array.isArray(results) && results.length > 0;
        const hasBanners = Array.isArray(banners) && banners.length > 0;
        if (hasResults === hasBanners) {
          throw new Error(
            'listing clickthrough requires exactly one of results or banners with one item'
          );
        }
        if (hasResults && results.length !== 1) {
          throw new Error(
            'listing product clickthrough requires results length 1'
          );
        }
        if (hasBanners && banners.length !== 1) {
          throw new Error(
            'listing banner clickthrough requires banners length 1'
          );
        }
      } else if (eventType === 'addtocart') {
        if (!Array.isArray(results) || results.length === 0) {
          throw new Error('results are required for listing addtocart');
        }
      } else {
        throw new Error(`Unsupported listing eventType: ${eventType}`);
      }

      const userId = await getIdOrGenerateId('ssUserId');
      const sessionId = await getIdOrGenerateId('ssSessionIdNamespace');
      const pageLoadId = nanoid();

      const context = {
        userId,
        sessionId,
        pageLoadId,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        initiator: BEACON_V2_INITIATOR,
      };
      if (dev) {
        context.dev = 'true';
      }

      const endpoint = `${BEACON_V2_BASE}/${siteId}/${resource}/${eventType}`;

      let data;
      if (eventType === 'render') {
        data = { responseId };
      } else if (eventType === 'redirect') {
        data = { responseId, redirect };
      } else if (eventType === 'impression') {
        data = { responseId, results, banners };
      } else if (eventType === 'clickthrough') {
        if (Array.isArray(results) && results.length === 1) {
          data = { responseId, results };
        } else {
          data = { responseId, banners };
        }
      } else {
        data = { responseId, results };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ context, data }),
        keepalive: true,
      });

      if (!res.ok) {
        const errBody = await res.text();
        const msg = `Searchspring listing Beacon 2.0 ${scope}/${eventType} failed: ${res.status} ${res.statusText}`;
        throw new Error(errBody ? `${msg} - ${errBody}` : msg);
      }

      return res.json().catch(() => ({}));
    },
    [getIdOrGenerateId]
  );

  return { sendEventsToBeacon, sendEventsToBeaconV2, sendListingBeaconV2 };
};
